import { Controller } from "@hotwired/stimulus";

const SAVE_DEBOUNCE_MS = 180;
const PAYLOAD_VERSION = 2;
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const LEGACY_PREPARATION_DRAFT_KEY_PATTERN = /^evercooked:form-draft:preparation:(?:new|\d+:edit)$/;
const SENSITIVE_NAME_PATTERN = /(authenticity_token|token|secret|api[_-]?key|credit|card|cvv|cvc|ssn|social[_-]?security)/i;
const PERSISTABLE_INPUT_TYPES = new Set([ "hidden", "text", "search", "url", "tel", "email", "number", "date" ]);

export default class extends Controller {
  static targets = [ "field", "restoredNotice" ];
  static values = {
    key: String,
    clearKey: String,
    cleanupLegacyPreparation: Boolean,
    recipeSnapshot: Boolean,
    skipPersistence: Boolean,
    skipRestore: Boolean,
    skipStorage: Boolean
  };

  initialize() {
    this.saveTimer = null;
    this.isDirty = false;
    this.isDiscarding = false;
    this.isApplyingSnapshot = false;
    this.listenersActive = false;
    this.boundFields = new Set();
    this.boundScheduleSave = this.scheduleSave.bind(this);
    this.boundPersistNow = this.persistNow.bind(this);
    this.boundApplyRecipeSnapshotFields = this.applyRecipeSnapshotFields.bind(this);
    this.boundRecipeSnapshotRestoreFailed = this.handleRecipeSnapshotRestoreFailed.bind(this);
    this.boundPersistCurrentSnapshot = this.persistCurrentSnapshot.bind(this);
    this.boundRestoreAfterHistory = this.restoreAfterHistory.bind(this);
    this.recipeSnapshotListenersActive = false;
    this.historyListenersActive = false;
    this.pendingRecipeSnapshotRestore = null;
  }

  connect() {
    if (this.cleanupLegacyPreparationValue) {
      this.removeLegacyPreparationDrafts();
    }

    if (this.hasClearKeyValue) {
      this.removeStoredDraft(this.clearKeyValue);
      return;
    }

    if (!this.hasKeyValue || this.keyValue.trim() === "") return;

    if (this.skipStorageValue) return;

    this.element.addEventListener("form-draft:apply-recipe-snapshot-fields", this.boundApplyRecipeSnapshotFields);
    this.element.addEventListener("form-draft:recipe-snapshot-restore-failed", this.boundRecipeSnapshotRestoreFailed);
    this.element.addEventListener("form-draft:persist-current-snapshot", this.boundPersistCurrentSnapshot);
    this.recipeSnapshotListenersActive = true;

    if (this.skipPersistenceValue) {
      this.removeStoredDraft(this.keyValue);
      return;
    }

    this.initialSnapshot = this.captureSnapshot();
    this.listenersActive = true;
    this.fieldTargets.forEach((field) => this.bindField(field));

    window.addEventListener("beforeunload", this.boundPersistNow);
    document.addEventListener("turbo:before-cache", this.boundPersistNow);
    this.element.addEventListener("turbo:submit-start", this.boundPersistNow);

    if (this.skipRestoreValue) {
      this.removeStoredDraft(this.keyValue);
      return;
    }

    document.addEventListener("turbo:render", this.boundRestoreAfterHistory);
    window.addEventListener("pageshow", this.boundRestoreAfterHistory);
    this.historyListenersActive = true;
    this.restoreStoredDraft();
  }

  disconnect() {
    this.listenersActive = false;
    this.boundFields.forEach((field) => this.unbindField(field));

    if (this.recipeSnapshotListenersActive) {
      this.element.removeEventListener("form-draft:apply-recipe-snapshot-fields", this.boundApplyRecipeSnapshotFields);
      this.element.removeEventListener("form-draft:recipe-snapshot-restore-failed", this.boundRecipeSnapshotRestoreFailed);
      this.element.removeEventListener("form-draft:persist-current-snapshot", this.boundPersistCurrentSnapshot);
      this.recipeSnapshotListenersActive = false;
    }

    if (this.boundPersistNow) {
      window.removeEventListener("beforeunload", this.boundPersistNow);
      document.removeEventListener("turbo:before-cache", this.boundPersistNow);
      this.element.removeEventListener("turbo:submit-start", this.boundPersistNow);
    }

    if (this.historyListenersActive) {
      document.removeEventListener("turbo:render", this.boundRestoreAfterHistory);
      window.removeEventListener("pageshow", this.boundRestoreAfterHistory);
      this.historyListenersActive = false;
    }

    this.persistNow();
  }

  fieldTargetConnected(field) {
    if (this.listenersActive) this.bindField(field);
  }

  fieldTargetDisconnected(field) {
    this.unbindField(field);
  }

  bindField(field) {
    if (!this.isPersistableField(field) || this.boundFields.has(field)) return;

    field.addEventListener("input", this.boundScheduleSave);
    field.addEventListener("change", this.boundScheduleSave);
    this.boundFields.add(field);
  }

  unbindField(field) {
    if (!this.boundFields.has(field)) return;

    field.removeEventListener("input", this.boundScheduleSave);
    field.removeEventListener("change", this.boundScheduleSave);
    this.boundFields.delete(field);
  }

  discard(event) {
    event?.preventDefault();
    if (!this.hasKeyValue) return;

    if (this.recipeSnapshotValue) {
      this.isDiscarding = true;
      this.pendingRecipeSnapshotRestore = null;
      this.isDirty = false;
      this.clearSaveTimer();
      this.removeStoredDraft(this.keyValue);
      window.Turbo?.cache?.clear?.();
      window.location.reload();
      return;
    }

    this.clearSaveTimer();
    this.removeStoredDraft(this.keyValue);

    this.applySnapshot(this.initialSnapshot);
    this.isDirty = false;

    if (this.hasRestoredNoticeTarget) {
      this.restoredNoticeTarget.classList.add("hidden");
    }
  }

  scheduleSave(event) {
    if (this.isDiscarding) return;
    if (this.isApplyingSnapshot) return;
    if (!this.hasKeyValue) return;
    if (!this.isPersistableField(event?.target)) return;

    this.isDirty = true;
    this.clearSaveTimer();
    this.saveTimer = window.setTimeout(() => {
      this.saveTimer = null;
      this.persistDraft();
    }, SAVE_DEBOUNCE_MS);
  }

  persistNow(event) {
    this.clearSaveTimer();
    if (this.isDiscarding) return;
    if (this.isDirty) this.persistDraft();

    if (event?.type === "turbo:before-cache") this.showStoredDraftNotice();
  }

  persistDraft() {
    const key = this.keyValue;
    if (!key || !this.isDirty || this.isDiscarding) return;

    const fields = {};
    this.persistableFields().forEach((field) => {
      const identifier = this.fieldIdentifier(field);
      if (!identifier) return;

      if (field.type === "radio") {
        if (field.checked || !Object.prototype.hasOwnProperty.call(fields, identifier)) {
          fields[identifier] = this.serializeField(field);
        }
        return;
      }

      fields[identifier] = this.serializeField(field);
    });

    if (Object.keys(fields).length === 0) {
      this.removeStoredDraft(key);
      this.isDirty = false;
      return;
    }

    const savedAt = Date.now();

    try {
      window.localStorage.setItem(
        key,
        JSON.stringify({
          version: PAYLOAD_VERSION,
          savedAt,
          expiresAt: savedAt + DRAFT_TTL_MS,
          fields
        })
      );
      this.isDirty = false;
    } catch (_error) {
      // Ignore storage quota and privacy-mode failures.
    }
  }

  restoreDraft() {
    const payload = this.validStoredDraftPayload(this.keyValue);
    const restoredCount = payload ? this.applyFieldValues(payload.fields) : 0;
    this.dispatchRestoredFields();
    return restoredCount;
  }

  validStoredDraftPayload(key) {
    const payload = this.readStoredDraftPayload(key);
    if (!payload) return null;

    if (!this.isValidDraftPayload(payload)) {
      this.removeStoredDraft(key);
      return null;
    }

    return payload;
  }

  recipeSnapshotFromPayload(payload) {
    if (!this.recipeSnapshotValue || !payload) return { recipeId: "", snapshotOnly: false };

    const recipeField = this.element.querySelector("[data-preparation-recipe-target='selection']");
    const identifier = this.fieldIdentifier(recipeField) || "preparation_recipe_id";
    const recipeId = String(payload.fields[identifier] || "").trim();
    if (recipeId) return { recipeId, snapshotOnly: false };

    const snapshotTemplateField = this.element.querySelector("[data-preparation-recipe-target='snapshotTemplate']");
    const snapshotTemplateIdentifier = this.fieldIdentifier(snapshotTemplateField) || "snapshot_template_recipe_id";
    const snapshotTemplateRecipeId = String(payload.fields[snapshotTemplateIdentifier] || "").trim();
    return { recipeId: snapshotTemplateRecipeId, snapshotOnly: Boolean(snapshotTemplateRecipeId) };
  }

  deferRecipeSnapshotRestore(payload, recipeId, snapshotOnly = false) {
    this.pendingRecipeSnapshotRestore = payload;
    this.isApplyingSnapshot = true;
    this.showRestoredNotice(Math.max(Object.keys(payload.fields || {}).length, 1));
    this.scrollToRestoredDraftNotice();

    window.setTimeout(() => {
      if (this.isDiscarding || this.pendingRecipeSnapshotRestore !== payload) return;

      this.element.dispatchEvent(new CustomEvent("form-draft:restore-recipe-snapshot", {
        bubbles: true,
        detail: { fields: payload.fields, recipeId, snapshotOnly }
      }));
    }, 0);
  }

  applyRecipeSnapshotFields(event) {
    if (this.isDiscarding) return;

    const fields = event.detail?.fields;
    if (!fields) return;

    this.clearSaveTimer();
    const restoredFields = event.detail?.snapshotOnly === true
      ? fields
      : Object.fromEntries(Object.entries(fields).filter(([identifier]) => identifier !== "snapshot_template_recipe_id"));
    const restoredCount = this.applyFieldValues(restoredFields);
    this.pendingRecipeSnapshotRestore = null;
    this.isDirty = false;
    this.isApplyingSnapshot = false;
    this.dispatchRestoredFields();
    this.showRestoredNotice(Math.max(restoredCount, 1));
    this.scrollToRestoredDraftNotice();
  }

  handleRecipeSnapshotRestoreFailed() {
    if (this.isDiscarding) return;

    const payload = this.pendingRecipeSnapshotRestore;
    this.pendingRecipeSnapshotRestore = null;
    this.clearSaveTimer();
    this.isApplyingSnapshot = false;

    const restoredFields = payload ? this.nonRecipeSnapshotFields(payload.fields) : {};
    const restoredCount = this.applyFieldValues(restoredFields);
    this.isDirty = true;
    this.persistDraft();
    this.showRestoredNotice(restoredCount);
    this.scrollToRestoredDraftNotice();
  }

  persistCurrentSnapshot() {
    if (!this.hasKeyValue || this.skipStorageValue || this.isDiscarding) return;

    this.clearSaveTimer();
    this.isDirty = true;
    window.setTimeout(() => this.persistDraft(), 0);
  }

  nonRecipeSnapshotFields(fields) {
    return Object.fromEntries(Object.entries(fields || {}).filter(([identifier]) => (
      identifier !== "preparation_recipe_id" && identifier !== "snapshot_template_recipe_id"
    )));
  }

  applyFieldValues(fields) {
    if (!fields || typeof fields !== "object" || Array.isArray(fields)) return 0;

    let restoredCount = 0;

    this.isApplyingSnapshot = true;
    this.persistableFields().forEach((field) => {
      const identifier = this.fieldIdentifier(field);
      if (!identifier || !Object.prototype.hasOwnProperty.call(fields, identifier)) return;

      const restored = this.applySerializedField(field, fields[identifier]);
      if (!restored) return;

      restoredCount += 1;
      this.dispatchRestoreEvents(field);
    });
    this.isApplyingSnapshot = false;

    return restoredCount;
  }

  showRestoredNotice(restoredCount) {
    if (!this.hasRestoredNoticeTarget) return;

    this.restoredNoticeTarget.classList.toggle("hidden", restoredCount <= 0);
  }

  scrollToRestoredDraftNotice() {
    if (!this.recipeSnapshotValue || !this.hasRestoredNoticeTarget) return;

    const notice = this.restoredNoticeTarget;
    window.requestAnimationFrame(() => {
      if (this.isDiscarding || notice.classList.contains("hidden")) return;

      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    });
  }

  showStoredDraftNotice() {
    const payload = this.validStoredDraftPayload(this.keyValue);
    this.showRestoredNotice(payload ? Object.keys(payload.fields).length : 0);
  }

  restoreAfterHistory(event) {
    if (this.isDiscarding) return;
    if (event?.type === "pageshow" && event.persisted !== true) return;

    this.restoreStoredDraft();
  }

  restoreStoredDraft() {
    const storedPayload = this.validStoredDraftPayload(this.keyValue);
    const recipeSnapshot = this.recipeSnapshotFromPayload(storedPayload);
    if (recipeSnapshot.recipeId) {
      this.deferRecipeSnapshotRestore(storedPayload, recipeSnapshot.recipeId, recipeSnapshot.snapshotOnly);
      return;
    }

    const restoredCount = this.restoreDraft();
    this.showRestoredNotice(storedPayload ? Math.max(restoredCount, 1) : 0);
    if (storedPayload) this.scrollToRestoredDraftNotice();
  }

  isValidDraftPayload(payload) {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) return false;
    if (payload.version !== PAYLOAD_VERSION) return false;
    if (!Number.isInteger(payload.savedAt) || !Number.isInteger(payload.expiresAt)) return false;
    if (payload.savedAt > payload.expiresAt) return false;
    if (payload.expiresAt - payload.savedAt > DRAFT_TTL_MS) return false;
    if (payload.expiresAt <= Date.now()) return false;
    if (!payload.fields || typeof payload.fields !== "object" || Array.isArray(payload.fields)) return false;

    const fieldEntries = Object.entries(payload.fields);
    return fieldEntries.length > 0 && fieldEntries.every(([identifier, value]) => (
      identifier.trim() !== "" && typeof value === "string"
    ));
  }

  captureSnapshot() {
    const snapshot = {};

    this.persistableFields().forEach((field) => {
      const identifier = this.fieldIdentifier(field);
      if (!identifier) return;

      snapshot[identifier] = this.serializeField(field);
    });

    return snapshot;
  }

  applySnapshot(snapshot) {
    this.applyFieldValues(snapshot);
  }

  persistableFields() {
    return this.fieldTargets.filter((field) => this.isPersistableField(field));
  }

  isPersistableField(field) {
    if (!field) return false;
    if (field.disabled) return false;

    const identity = `${field.name || ""} ${field.id || ""}`.trim();
    if (identity === "") return false;
    if (SENSITIVE_NAME_PATTERN.test(identity)) return false;

    if (field.tagName === "TEXTAREA") return true;
    if (field.tagName === "SELECT") return true;
    if (field.tagName !== "INPUT") return false;

    const type = (field.type || "").toLowerCase();
    return PERSISTABLE_INPUT_TYPES.has(type) || (
      type === "radio" && field.dataset.preparationRecipeTarget === "pathOption"
    );
  }

  fieldIdentifier(field) {
    return field.id || field.name || null;
  }

  serializeField(field) {
    if (field.type === "radio") return field.checked ? field.value : "";

    return field.value;
  }

  applySerializedField(field, value) {
    const serializedValue = value === null || value === undefined ? "" : String(value);
    if (field.type === "radio") {
      const checked = serializedValue === field.value;
      if (field.checked === checked) return false;

      field.checked = checked;
      return true;
    }

    if (field.value === serializedValue) return false;

    field.value = serializedValue;
    return true;
  }

  dispatchRestoreEvents(field) {
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
  }

  dispatchRestoredFields() {
    this.element.dispatchEvent(new CustomEvent("form-draft:restored-fields", { bubbles: true }));
  }

  readStoredDraftPayload(key) {
    if (!key) return null;

    let raw = null;
    try {
      raw = window.localStorage.getItem(key);
    } catch (_error) {
      return null;
    }
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch (_error) {
      this.removeStoredDraft(key);
      return null;
    }
  }

  removeStoredDraft(key) {
    if (!key) return;

    try {
      window.localStorage.removeItem(key);
    } catch (_error) {
      // Ignore storage access failures.
    }
  }

  removeLegacyPreparationDrafts() {
    let storedKeys;

    try {
      storedKeys = Object.keys(window.localStorage);
    } catch (_error) {
      return;
    }

    storedKeys
      .filter((key) => LEGACY_PREPARATION_DRAFT_KEY_PATTERN.test(key))
      .forEach((key) => this.removeStoredDraft(key));
  }

  clearSaveTimer() {
    if (!this.saveTimer) return;

    window.clearTimeout(this.saveTimer);
    this.saveTimer = null;
  }
}
